"use client"

import { Dumbbell, RotateCcw, Zap, Copy, ThumbsUp, Plus } from "lucide-react"
import { useState } from "react"

const ChatMessage = ({ message, onRetry, onCopySuccess }) => {
  const [showEnhancedModal, setShowEnhancedModal] = useState(false)
  const [copied, setCopied] = useState(false)

  const handleCopy = () => {
    navigator.clipboard.writeText(message.text)
    setCopied(true)
    onCopySuccess?.()
    setTimeout(() => setCopied(false), 2000)
  }

  const handleAddToInput = (query) => {
    const inputElement = document.querySelector('input[type="text"]')
    if (inputElement) {
      // Trigger the onChange event properly
      const event = new Event("input", { bubbles: true })
      inputElement.value = query
      inputElement.dispatchEvent(event)
      inputElement.focus()
      setShowEnhancedModal(false)
    }
  }

  const parseText = (text) => {
    const parts = text.split(/(\*\*.*?\*\*)/);
    return parts.map((part, index) => {
      if (part.startsWith("**") && part.endsWith("**")) {
        return <strong key={index}>{part.slice(2, -2)}</strong>;
      }
      return part;
    });
  };

  const renderMessageContent = (text) => {
    if (/<\/?[a-z][\s\S]*>/i.test(text)) {
      // Render as HTML if it contains HTML tags
      return <div dangerouslySetInnerHTML={{ __html: text }} />;
    } else {
      // Render plain text (with markdown formatting)
      return <p className="message-text">{parseText(text)}</p>;
    }
  };

  return (
    <div className={`flex ${message.sender === "user" ? "justify-end" : "justify-start"} mb-8 group`}>
      <div className={`w-full overflow-x-auto ${message.sender === "user" ? "order-2" : "order-1"}`}>

        {message.sender === "bot" && (
          <div className="flex items-center gap-3 mb-3">
            <div className="relative">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500 rounded-xl flex items-center justify-center shadow-lg">
                <Dumbbell className="w-5 h-5 text-white" />
              </div>
              <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-400 rounded-full border-2 border-white"></div>
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-gray-700">Metabuddy AI</span>
                {/*{message.refinedQuery && (
                  <button
                    onClick={() => setShowEnhancedModal(true)}
                    className="flex items-center gap-1 text-xs text-blue-600 bg-blue-50 hover:bg-blue-100 px-2 py-1 rounded-full font-medium border border-blue-200 transition-all duration-200 hover:scale-105"
                  >
                    <Zap className="w-3 h-3" />
                    Enhanced
                  </button>
                )}*/}
              </div>
              <div className="text-xs text-gray-500 mt-1">{message.timestamp}</div>
            </div>
          </div>
        )}

        {message.sender === "user" && (
          <div className="text-right mb-2">
            <span className="text-xs text-gray-500">{message.timestamp}</span>
          </div>
        )}

        <div
          className={`relative p-4 rounded-2xl shadow-lg transition-all duration-300 ${
            message.sender === "user"
              ? "bg-gradient-to-r from-blue-500 to-purple-600 text-white"
              : message.isError
                ? "bg-red-50 border-2 border-red-200 text-red-800"
                : "bg-white/90 backdrop-blur-sm border border-gray-200/50 text-gray-800"
          }`}
        >
        <div className="overflow-x-auto">
          <p className="text-sm whitespace-pre-wrap leading-relaxed inline-block min-w-fit">
            {renderMessageContent(message.text)}
          </p>
        </div>

          {message.sender === "bot" && !message.isError && (
            <div className="flex items-center gap-2 mt-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
              <button
                onClick={handleCopy}
                className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors text-gray-500 hover:text-gray-700"
                title="Copy message"
              >
                <Copy className="w-3 h-3" />
              </button>
              <button
                className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors text-gray-500 hover:text-gray-700"
                title="Like response"
              >
                {/*<ThumbsUp className="w-3 h-3" />*/}
              </button>
            </div>
          )}

          {message.isError && (
            <button
              onClick={() => onRetry(message.originalQuery)}
              className="mt-3 text-xs bg-red-500 text-white px-4 py-2 rounded-xl hover:bg-red-600 transition-colors duration-200 flex items-center gap-2 font-medium"
            >
              <RotateCcw className="w-3 h-3" />
              Retry
            </button>
          )}

          {copied && (
            <div className="absolute -top-8 left-4 bg-gray-900 text-white text-xs px-2 py-1 rounded animate-in slide-in-from-bottom-2 duration-200">
              Copied!
            </div>
          )}
        </div>
      </div>

      {/* Enhanced Query Modal */}
      {showEnhancedModal && message.refinedQuery && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full animate-in slide-in-from-bottom-4 duration-300">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
                  <Zap className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800">Enhanced Query</h3>
                  <p className="text-xs text-gray-500">AI-optimized version of your question</p>
                </div>
              </div>

              <div className="bg-gray-50 rounded-xl p-4 mb-4">
                <p className="text-sm text-gray-700 leading-relaxed">{message.refinedQuery}</p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowEnhancedModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors duration-200 font-medium"
                >
                  Close
                </button>
                <button
                  onClick={() => handleAddToInput(message.refinedQuery)}
                  className="flex-1 px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl hover:from-blue-600 hover:to-purple-700 transition-all duration-200 font-medium flex items-center justify-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Use Query
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ChatMessage
